import React, { useEffect, useState } from 'react';
import { Table, Typography, Empty, Input, Tooltip, message } from 'antd';
import dayjs from '_helpers/datetime';
import { useFetchWrapper } from '_helpers';
import { ROLES } from '_helpers/roles';
import { formatSlots } from '_helpers/schedule';

export { StudentProfileCard };

const { Text } = Typography;

// ── Shared styles ─────────────────────────────────────────────────────────────
const LABEL_BG   = 'var(--bg-subtle)';
const BORDER     = '1px solid var(--line)';

const tblStyle  = { width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: 12 };
const secCell   = { backgroundColor: 'var(--bg-page)', color: 'var(--ink)', padding: '6px 12px', fontWeight: 600, border: BORDER };
const lblCell   = { backgroundColor: LABEL_BG, padding: '5px 10px', border: BORDER, fontWeight: 500, verticalAlign: 'middle', width: '22%' };
const valCell   = { padding: '5px 10px', border: BORDER, verticalAlign: 'middle', width: '28%' };
const EDIT_HOVER = { cursor: 'text', borderRadius: 'var(--r-ctl)', padding: '1px 4px', display: 'block', minHeight: 20 };

const Dash = () => <span style={{ color: 'var(--ink-4)' }}>—</span>;

function Sec({ icon, title }) {
    return (
        <tr>
            <td colSpan={4} style={secCell}>
                {icon && <span style={{ marginRight: 6 }}>{icon}</span>}{title}
            </td>
        </tr>
    );
}

// Read-only two-value row
function Row2({ l1, v1, l2, v2 }) {
    const show = v => (v !== undefined && v !== null && v !== '') ? v : <Dash />;
    return (
        <tr>
            <td style={lblCell}>{l1}</td><td style={valCell}>{show(v1)}</td>
            <td style={lblCell}>{l2}</td><td style={valCell}>{show(v2)}</td>
        </tr>
    );
}

// ── Payment log columns ────────────────────────────────────────────────────────
const paymentColumns = [
    { title: 'Ngày thanh toán', dataIndex: 'payment_date', key: 'payment_date', width: 130,
      render: v => v ? dayjs(v).format('DD/MM/YYYY') : <Dash /> },
    { title: 'Số tiền', dataIndex: 'amount', key: 'amount', width: 130,
      render: v => v != null ? <Text strong style={{ color: 'var(--ink)' }}>{Number(v).toLocaleString('vi-VN')} đ</Text> : <Dash /> },
    { title: 'Phương thức TT', dataIndex: 'payment_method', key: 'payment_method', width: 130,
      render: m => m ? <span className={m === 'Tiền mặt' ? 'tag tag-info' : 'tag tag-neutral'}>{m}</span> : <Dash /> },
    { title: 'Ghi chú', dataIndex: 'notes', key: 'notes',
      render: n => n || <Dash /> },
];

// ── Main component ────────────────────────────────────────────────────────────
function StudentProfileCard({ data, classInfo: classInfoProp, archivedClasses = [], onUpdate, onClassUpdate }) {
    const fetchWrapper = useFetchWrapper();
    const userData = JSON.parse(localStorage.getItem('userData'));

    const [payments,      setPayments]      = useState([]);
    const [loadPayments,  setLoadPayments]  = useState(false);

    // ── student inline-edit ───────────────────────────────────────────────────
    const [editCell,   setEditCell]   = useState(null);  // { field, value, isDate? }
    const [saving,     setSaving]     = useState(false);

    // ── class inline-edit ─────────────────────────────────────────────────────
    const [editClassCell, setEditClassCell] = useState(null); // { field, value, isDate? }
    const [savingClass,   setSavingClass]   = useState(false);

    // ── local class info (updated optimistically) ─────────────────────────────
    const [localClass,  setLocalClass]  = useState(classInfoProp || null);

    // Sync when prop changes (e.g. different student opened)
    useEffect(() => {
        setLocalClass(classInfoProp || null);
        setEditClassCell(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classInfoProp?.class_id]);

    const cls = localClass; // convenience alias

    // ── formatted dates for the student ──────────────────────────────────────
    const dob = data?.date_of_birth ? dayjs(data.date_of_birth).format('DD/MM/YYYY') : null;
    const age = data?.date_of_birth ? dayjs().diff(dayjs(data.date_of_birth), 'years') : null;

    // ── formatted dates for the class ────────────────────────────────────────
    const fmtDate = ts => (ts ? dayjs(ts).format('DD/MM/YYYY') : null);

    // ─────────────────────────────────────────────────────────────────────────
    // Save student field edit
    // ─────────────────────────────────────────────────────────────────────────
    async function saveCardEdit(cell) {
        if (!cell || saving || !data?._id) return;
        setSaving(true);
        try {
            const body = new URLSearchParams();
            let apiValue = cell.value;
            if (cell.isDate) {
                const m = dayjs(cell.value, 'DD/MM/YYYY', true);
                if (!m.isValid()) {
                    message.error('Định dạng ngày không hợp lệ (DD/MM/YYYY)');
                    setSaving(false);
                    return;
                }
                apiValue = m.valueOf();
            }
            body.append(cell.field, apiValue);
            const res  = await fetchWrapper.put(
                `/api/manager/students/${data._id}`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                if (onUpdate) {
                    const storedVal = cell.isDate ? Number(apiValue) : cell.value;
                    onUpdate({ [cell.field]: storedVal });
                }
            } else {
                message.error(json.message || 'Lỗi khi lưu');
            }
        } catch (e) {
            message.error('Lỗi khi lưu: ' + e.message);
        }
        setSaving(false);
        setEditCell(null);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Save class field edit
    // ─────────────────────────────────────────────────────────────────────────
    async function saveClassEdit(cell) {
        if (!cell || savingClass || !cls) return;
        setSavingClass(true);
        try {
            const body = new URLSearchParams();
            let apiValue = cell.value;
            if (cell.isDate) {
                if (cell.value === '' || cell.value == null) {
                    apiValue = '';   // clears the date
                } else {
                    const m = dayjs(cell.value, 'DD/MM/YYYY', true);
                    if (!m.isValid()) {
                        message.error('Định dạng ngày không hợp lệ (DD/MM/YYYY)');
                        setSavingClass(false);
                        return;
                    }
                    apiValue = m.valueOf();
                }
            }
            body.append(cell.field, apiValue);
            const res  = await fetchWrapper.put(
                `/api/manager/classes/${cls.class_id}/info`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                const storedVal = cell.isDate
                    ? (apiValue ? Number(apiValue) : null)
                    : cell.value;
                setLocalClass(prev => ({ ...prev, [cell.field]: storedVal }));
                if (onClassUpdate) onClassUpdate(cls.class_id, { [cell.field]: storedVal });
            } else {
                message.error(json.message || 'Lỗi khi lưu lớp');
            }
        } catch (e) {
            message.error('Lỗi khi lưu: ' + e.message);
        }
        setSavingClass(false);
        setEditClassCell(null);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render helpers
    // ─────────────────────────────────────────────────────────────────────────
    function editableVal(field, displayValue, rawValue, isDate) {
        const active = editCell?.field === field;
        if (active) {
            return (
                <Input
                    autoFocus size="small"
                    value={editCell.value}
                    placeholder={isDate ? 'DD/MM/YYYY' : undefined}
                    onChange={e => setEditCell({ ...editCell, value: e.target.value })}
                    onBlur={() => saveCardEdit(editCell)}
                    onPressEnter={() => saveCardEdit(editCell)}
                    style={{ minWidth: 60, width: '100%' }}
                />
            );
        }
        return (
            <Tooltip title="Nhấp đúp để chỉnh sửa" mouseEnterDelay={0.8}>
                <span
                    style={EDIT_HOVER}
                    onDoubleClick={() => {
                        if (!saving) setEditCell({ field, value: rawValue ?? displayValue ?? '', isDate: !!isDate });
                    }}
                >
                    {displayValue ? displayValue : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                </span>
            </Tooltip>
        );
    }

    function editableClassVal(field, displayValue, rawValue, isDate) {
        const active = editClassCell?.field === field;
        if (active) {
            return (
                <Input
                    autoFocus size="small"
                    value={editClassCell.value}
                    placeholder={isDate ? 'DD/MM/YYYY' : undefined}
                    onChange={e => setEditClassCell({ ...editClassCell, value: e.target.value })}
                    onBlur={() => saveClassEdit(editClassCell)}
                    onPressEnter={() => saveClassEdit(editClassCell)}
                    style={{ minWidth: 60, width: '100%' }}
                />
            );
        }
        return (
            <Tooltip title="Nhấp đúp để chỉnh sửa" mouseEnterDelay={0.8}>
                <span
                    style={EDIT_HOVER}
                    onDoubleClick={() => {
                        if (!savingClass) setEditClassCell({ field, value: rawValue ?? displayValue ?? '', isDate: !!isDate });
                    }}
                >
                    {displayValue ? displayValue : <span style={{ color: 'var(--ink-4)' }}>—</span>}
                </span>
            </Tooltip>
        );
    }

    // ── fetch payment log ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!data) return;
        let cancelled = false;
        async function fetchPayments() {
            setLoadPayments(true);
            try {
                // Use the single-student endpoint — the list view (/api/tuition with no
                // query) returns summaries only and omits the records array.
                const res  = await fetchWrapper.get(`/api/tuition?student_id=${encodeURIComponent(data.student_id)}`);
                const json = await res.json();
                if (!cancelled && json.status === 'Success') {
                    setPayments(json.message?.[0]?.records || []);
                }
            } catch {}
            if (!cancelled) setLoadPayments(false);
        }
        fetchPayments();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.student_id]);

    if (!data) return null;

    return (
        <div style={{ padding: '4px 0' }}>
            <div style={{ color: 'var(--ink-4)', fontSize: 11, marginBottom: 8 }}>
                Nhấp đúp vào ô để chỉnh sửa trực tiếp
            </div>

            {/* ── Thông tin học sinh ─────────────────────────────────────── */}
            <table className="profile-card-table" style={tblStyle}>
                <tbody>
                    <Sec icon="🪪" title="Thông tin học sinh" />
                    <tr>
                        <td style={lblCell}>Mã số HS</td>
                        <td style={valCell}>{userData?.role === ROLES.MANAGER
                            ? editableVal('student_id', data.student_id, data.student_id)
                            : data.student_id}</td>
                        <td style={lblCell}>Ngày tháng năm sinh</td>
                        <td style={valCell}>{editableVal('date_of_birth', dob, dob, true)}</td>
                    </tr>
                    <tr>
                        <td style={lblCell}>Họ và tên</td>
                        <td style={valCell}>{editableVal('name', data.name, data.name)}</td>
                        <td style={lblCell}>Tuổi</td>
                        <td style={valCell}>{age != null ? String(age) : <Dash />}</td>
                    </tr>
                    <tr>
                        <td style={lblCell}>Số điện thoại HS</td>
                        <td style={valCell}>{editableVal('phone_number', data.phone_number, data.phone_number)}</td>
                        <td style={lblCell}>Giới tính</td>
                        <td style={valCell}>
                            {data.gender === 'male' ? 'Nam' : data.gender === 'female' ? 'Nữ' : <Dash />}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* ── Thông tin phụ huynh ───────────────────────────────────── */}
            <table className="profile-card-table" style={tblStyle}>
                <tbody>
                    <Sec icon="❤️" title="Thông tin phụ huynh" />
                    <tr>
                        <td style={lblCell}>Họ và tên PH liên lạc chính</td>
                        <td style={valCell}>{editableVal('parent_name', data.parent_name, data.parent_name)}</td>
                        <td style={lblCell}>Số điện thoại chính</td>
                        <td style={valCell}>{editableVal('parent_number', data.parent_number, data.parent_number)}</td>
                    </tr>
                    <tr>
                        <td style={lblCell}>Email</td>
                        <td style={valCell}>{editableVal('email', data.email, data.email)}</td>
                        <td style={lblCell}>Địa chỉ</td>
                        <td style={valCell}>{editableVal('location', data.location, data.location)}</td>
                    </tr>
                </tbody>
            </table>

            {/* ── Thông tin lớp học ─────────────────────────────────────── */}
            <table className="profile-card-table" style={tblStyle}>
                <tbody>
                    <Sec icon="📚" title="Thông tin lớp học" />
                    <tr>
                        <td style={lblCell}>Lớp</td>
                        <td style={valCell}>{cls?.class_name || <Dash />}</td>
                        <td style={lblCell}>Giờ học</td>
                        <td style={valCell}>{formatSlots(cls?.schedule_slots) || <Dash />}</td>
                    </tr>
                    <Row2
                        l1="Giáo viên phụ trách"
                        v1={cls?.class_teacher?.name || null}
                        l2="Người take care"
                        v2={editableClassVal('caretaker', cls?.caretaker, cls?.caretaker)}
                    />
                    <tr>
                        <td style={lblCell}>Sách</td>
                        <td style={{ ...valCell, width: 'auto' }} colSpan={3}>
                            {editableClassVal('book', cls?.book, cls?.book)}
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* ── Thời gian học ─────────────────────────────────────────── */}
            <table className="profile-card-table" style={tblStyle}>
                <tbody>
                    <Sec icon="🕐" title="Thời gian học" />
                    <tr>
                        <td style={lblCell}>Ngày bắt đầu</td>
                        <td style={valCell}>
                            {editableClassVal('start_date', fmtDate(cls?.start_date), fmtDate(cls?.start_date), true)}
                        </td>
                        <td style={lblCell}>Ngày kết thúc</td>
                        <td style={valCell}>
                            {editableClassVal('end_date', fmtDate(cls?.end_date), fmtDate(cls?.end_date), true)}
                        </td>
                    </tr>
                    <Row2
                        l1="Số tháng đã học"     v1={null}
                        l2="Tổng số buổi đã học" v2={null}
                    />
                </tbody>
            </table>

            {/* ── Các lớp đã học (lưu trữ) ───────────────────────────────── */}
            {archivedClasses.length > 0 &&
                <table className="profile-card-table" style={tblStyle}>
                    <tbody>
                        <Sec icon="📦" title="Các lớp đã học" />
                        <tr>
                            <td style={{ ...valCell, width: 'auto' }} colSpan={4}>
                                {archivedClasses.map(c => c.class_name).join('  •  ')}
                            </td>
                        </tr>
                    </tbody>
                </table>}

            {/* ── Nhật kí thanh toán ────────────────────────────────────── */}
            <div style={{ border: BORDER, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                    padding: '6px 12px', fontWeight: 'bold', fontSize: 12,
                    borderBottom: BORDER, backgroundColor: 'var(--bg-subtle)',
                }}>
                    Nhật kí thanh toán
                </div>
                <Table
                    columns={paymentColumns}
                    dataSource={payments.map((p, i) => ({ ...p, key: p._id || i }))}
                    loading={loadPayments}
                    size="small"
                    pagination={false}
                    locale={{ emptyText: (
                        <Empty
                            description={<span style={{ color: 'var(--ink-4)', fontSize: 12 }}>Chưa có nhật kí thanh toán</span>}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}}
                    style={{ fontSize: 12 }}
                />
            </div>
        </div>
    );
}
